import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ComputerDesktopIcon, 
  CameraIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  BoltIcon,
  StarIcon
} from '@heroicons/react/24/outline';

export const HoverProductShowcase = () => {
  const [hoveredProduct, setHoveredProduct] = useState(null);

  const products = [
    {
      id: 1,
      name: "DICOMFiles",
      tagline: "Automatización Completa",
      description: "Reduce el trabajo manual en 80-90% con procesamiento automático 24/7",
      icon: <ComputerDesktopIcon className="h-12 w-12" />,
      features: [
        "Procesamiento 24/7",
        "Integración PACS",
        "Automatización PDFs",
        "Códigos QR"
      ],
      link: "/dicomfiles",
      cta: "Ver Detalles",
      gradient: "from-blue-500 to-blue-700",
      bgGradient: "from-blue-50 to-blue-100",
      accentColor: "blue",
      isPopular: true,
      stats: {
        reduction: "80-90%",
        processing: "24/7",
        compliance: "100%"
      }
    },
    {
      id: 2,
      name: "DICOMCapture",
      tagline: "Captura Avanzada",
      description: "Captura y procesamiento de imágenes DICOM desde equipos médicos",
      icon: <CameraIcon className="h-12 w-12" />,
      features: [
        "Captura automática",
        "Validación calidad",
        "Tiempo real",
        "Control flujo"
      ],
      link: "/contact",
      cta: "Más Información",
      gradient: "from-green-500 to-green-700",
      bgGradient: "from-green-50 to-green-100",
      accentColor: "green",
      isPopular: false,
      stats: {
        capture: "Automática",
        quality: "Validada",
        speed: "Tiempo real"
      }
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-green-100 text-gray-800 rounded-full text-sm font-medium mb-4">
            <SparklesIcon className="h-4 w-4 mr-2 text-blue-600" />
            Nuestros Software
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Soluciones DICOM
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">
              Interactivas
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Pasa el cursor sobre cada producto para descubrir sus características únicas
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {products.map((product) => (
            <div
              key={product.id}
              className="relative group"
              onMouseEnter={() => setHoveredProduct(product.id)}
              onMouseLeave={() => setHoveredProduct(null)}
            >
              {/* Main Card */}
              <div className={`relative overflow-hidden rounded-2xl shadow-lg transition-all duration-500 transform ${
                hoveredProduct === product.id ? 'scale-105 shadow-2xl' : 'scale-100'
              }`}>
                {/* Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${product.gradient} transition-all duration-500 ${
                  hoveredProduct === product.id ? 'opacity-100' : 'opacity-90'
                }`} />
                
                {/* Animated Background Elements */}
                <div className="absolute inset-0 opacity-10">
                  <div className={`absolute inset-0 transition-all duration-1000 ${
                    hoveredProduct === product.id ? 'animate-pulse' : ''
                  }`}>
                    <div className="absolute top-0 left-0 w-full h-full bg-white rounded-full transform scale-0 group-hover:scale-150 transition-transform duration-1000" />
                  </div>
                </div>

                {/* Content */}
                <div className="relative z-10 p-8 text-white">
                  {/* Header */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-4 rounded-xl bg-white bg-opacity-20 transition-all duration-300 ${
                        hoveredProduct === product.id ? 'bg-opacity-30 scale-110' : ''
                      }`}>
                        {product.icon}
                      </div>
                      {product.isPopular && (
                        <div className={`px-3 py-1 rounded-full bg-yellow-400 text-yellow-900 text-sm font-medium transition-all duration-300 ${
                          hoveredProduct === product.id ? 'scale-110' : ''
                        }`}>
                          <StarIcon className="h-4 w-4 inline mr-1" />
                          Popular
                        </div>
                      )}
                    </div>
                    
                    <h3 className={`text-3xl font-bold mb-2 transition-all duration-300 ${
                      hoveredProduct === product.id ? 'text-4xl' : ''
                    }`}>
                      {product.name}
                    </h3>
                    
                    <p className={`text-lg font-semibold mb-3 transition-all duration-300 ${
                      hoveredProduct === product.id ? 'text-xl' : ''
                    }`}>
                      {product.tagline}
                    </p>
                    
                    <p className="text-white/80 leading-relaxed">
                      {product.description}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <ul className="space-y-3">
                      {product.features.map((feature, index) => (
                        <li 
                          key={index} 
                          className={`flex items-center transition-all duration-300 ${
                            hoveredProduct === product.id ? 'transform translate-x-2' : ''
                          }`}
                          style={{ transitionDelay: `${index * 100}ms` }}
                        >
                          <CheckIcon className="h-5 w-5 text-green-300 mr-3 flex-shrink-0" />
                          <span className="text-white/90">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Stats */}
                  <div className={`mb-6 transition-all duration-500 ${
                    hoveredProduct === product.id ? 'opacity-100' : 'opacity-0'
                  }`}>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(product.stats).map(([key, value], index) => (
                        <div key={key} className="text-center">
                          <div className="text-lg font-bold text-white">{value}</div>
                          <div className="text-xs text-white/70 capitalize">{key}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-end">
                    <Link
                      to={product.link}
                      className={`flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-medium transition-all duration-300 hover:bg-gray-100 ${
                        hoveredProduct === product.id ? 'scale-110 shadow-lg' : ''
                      }`}
                    >
                      {product.cta}
                      <ArrowRightIcon className={`ml-2 h-4 w-4 transition-all duration-300 ${
                        hoveredProduct === product.id ? 'transform translate-x-1' : ''
                      }`} />
                    </Link>
                  </div>
                </div>

                {/* Hover Overlay Effect */}
                <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transition-all duration-500 ${
                  hoveredProduct === product.id ? 'opacity-10 animate-pulse' : ''
                }`} />

                {/* Floating Elements */}
                <div className="absolute top-4 right-4 opacity-0 transition-all duration-500 group-hover:opacity-100">
                  <BoltIcon className="h-6 w-6 text-yellow-300 animate-bounce" />
                </div>
                
                <div className="absolute bottom-4 left-4 opacity-0 transition-all duration-500 group-hover:opacity-100">
                  <SparklesIcon className="h-5 w-5 text-white animate-pulse" />
                </div>
              </div>

              {/* Glow Effect */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${product.gradient} rounded-2xl blur opacity-0 transition-all duration-500 ${
                hoveredProduct === product.id ? 'opacity-30' : ''
              }`} />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              ¿Necesitas ayuda para elegir?
            </h3>
            <p className="text-gray-600 mb-6">
              Nuestro equipo de expertos puede ayudarte a encontrar la solución perfecta
            </p>
            <Link
              to="/contact"
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-8 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-green-700 transition-all duration-200 inline-flex items-center"
            >
              Consultar con Experto
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
